<script setup>
import { ref, computed, onBeforeUnmount, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { formatBytes } from '@/lib/utils'

import { getI18n } from '@/plugins/i18n'

import { DxSplitter, DxItem } from 'devextreme-vue/splitter'
import { DxPieChart, DxSeries, DxLabel, DxConnector, DxFont, DxLegend } from 'devextreme-vue/pie-chart'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import { useColumns, useAttachment } from '@/composables/registry/service-attachment-grid'
import * as dataGrid from '@/composables/data-grid'

import AttachmentPreview from '@/components/shared/AttachmentPreview.vue'
import AttachmentUploaderPopup from '@/components/shared/AttachmentUploaderPopup.vue'
import CustomToolbarDeleteButtonTemplate from '@/components/shared/CustomToolbarDeleteButtonTemplate.vue'
import DocumentReferenceTemplate from '@/components/shared/DocumentReferenceTemplate.vue'

import { usePortalStore } from '@/stores/portal.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'

const gridId = 'service-attachment-grid'

const { gridRef, refreshGridAsync } = dataGrid.useGridRef()

const i18n = getI18n()
const route = useRoute()
const router = useRouter()

const portalStore = usePortalStore()
const registryStore = useRegistryStore()
const webSiteStore = useWebSiteStore()

const attachmentTimeout = null
const selectedRows = ref([])

const showUploadDialog = ref(false)
const chartData = ref(null)

const { openFile } = useAttachment()
const columns = useColumns()

const onSelectionChanged = async(e) => {
  selectedRows.value = e.selectedRowsData
}

const onToolbarPreparing = (e) => {
  dataGrid.useDefaultToolbar(e, {
    useDefaultAddButton: true,
    allowBatchDelete: true,
  })

  const items = e.toolbarOptions.items
  const addBtnIndex = items.findIndex(i => i.name === 'addRowButton')
  if (addBtnIndex !== -1) {
    items[addBtnIndex] = {
      widget: 'dxButton',
      location: 'after',
      name: 'addCustomRowButton',
      options: {
        icon: 'add',
        stylingMode: 'text',
        onClick: () => { showUploadDialog.value = true },
      },
    }
  }

  items.push(dataGrid.toolbarClearFilterIcon(e.component))
  items.push(dataGrid.toolbarRefreshIcon(e.component))
}

const editing = dataGrid.useDefaultEditing(null, {}, {
  allowUpdating: false,
  allowDeleting: true,
})

onBeforeUnmount(() => {
  if (attachmentTimeout) {
    clearTimeout(attachmentTimeout)
  }
})

const onUploaded = async() => {
  await getChartDataAsync()
  await refreshGridAsync()
}

const batchDeleteAsync = async(selectedIds) => {
  await registryStore.api.batchDeleteServiceAttachmentsAsync(selectedIds)
  await getChartDataAsync()
}

const getChartDataAsync = async() => {
  const response = await registryStore.api.getAttachmentChartDataAsync()
  chartData.value = response.data
}

const onRowRemoved = async() => { await getChartDataAsync() }

const dataSource = dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.serviceAttachmentsUrl, {
  customParamsFn: () => { return { $top: 500 } },
  avoidDuplicateDatasourceIds: true,
})
const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: dataSource,
  columns: columns,
  editing: editing,
  selection: dataGrid.useDefaultGridSelection(),
  height: '100%',
  groupPanel: {
    visible: true,
    emptyPanelText: i18n.t('common.groupPanelPlaceholder', 2)
  },
  grouping: {
    contextMenuEnabled: true,
    autoExpandAll: false,
  },
  remoteOperations: {
    filtering: true,
    sorting: true,
    paging: false,
    grouping: false,
  },
  rowDragging: {
    allowReordering: false,
  },
  onInitialized: (e) => dataGrid.useDefaultOnInitialized(e, {enableGrouping: true}),
  onToolbarPreparing: onToolbarPreparing,
  onSelectionChanged: onSelectionChanged,
  onRowRemoved: onRowRemoved,
  routerFilter: {
    fields: [
      {attachment_worksheet: 'attachment_worksheet_id'},
      {attachment_quote: 'attachment_quote_id'},
      {attachment_saledocument: 'attachment_saledocument_id'},
      {attachment_supplierorder: 'attachment_supplierorder_id'},
      {attachment_subject_ref: 'attachment_subject_ref'},
      {attachment_vehicle: 'attachment_vehicle_id'},
    ],
    getRoute: () => route,
    router: router
  }
})

const usedPercent = computed(() => (chartData.value?.used_storage / chartData.value?.total_storage) * 100)
const showBuyMoreStorage = computed(() => usedPercent.value > 70)
const storageData = computed(() => ([
  { label: i18n.t('common.usedStorage'), value: chartData.value?.used_storage || 0 },
  { label: i18n.t('common.remainingStorage'), value: chartData.value ? chartData.value.total_storage - chartData.value.used_storage : 1 },
]))

const customChartColor = ({ argument }) => {
  if (argument === i18n.t('common.remainingStorage')) {
    return { color: webSiteStore.storageColors.remaining }
  }
  if (usedPercent.value >= 90) return { color: webSiteStore.storageColors.usedDanger }
  if (usedPercent.value >= 70) return { color: webSiteStore.storageColors.usedWarning }
  return { color: webSiteStore.storageColors.usedOk }
}

onMounted(async() => {
  await getChartDataAsync()
})
</script>

<template>
  <div>
    <attachment-uploader-popup
      :show-dialog="showUploadDialog"
      @on-save="onUploaded(); showUploadDialog = false"
      @on-cancel="showUploadDialog = false"
    />

    <dx-splitter
      orientation="vertical"
      height="90vh"
    >
      <dx-item
        size="30%"
        min-size="21%"
        :collapsible="true"
        collapsed-size="21%"
      >
        <div class="flex flex-row w-full h-full justify-end">
          <div class="flex-1 max-w-fit">
            <dx-pie-chart
              v-if="chartData"
              :data-source="storageData"
              type="donut"
              :customize-point="customChartColor"
              center-template="centerTemplate"
              :size="{ width: 400, height: 220 }"
            >
              <dx-series
                argument-field="label"
                value-field="value"
              >
                <dx-label
                  :visible="true"
                  position="columns"
                  :customize-text="(data) => `${data.argumentText === i18n.t('common.usedStorage') ? `${data.argumentText}: ` : ''}${data.percentText} (${formatBytes(data.value)})`"
                >
                  <dx-connector
                    :visible="true"
                    :width="1"
                  />
                  <dx-font :size="11" />
                </dx-label>
              </dx-series>

              <dx-legend :visible="false" />

              <template #centerTemplate>
                <svg style="position:absolute;width:100%;height:100%">
                  <text
                    x="50%"
                    y="55%"
                    text-anchor="middle"
                    dy="0.3em"
                    fill="#666"
                    font-size="12"
                    font-weight="bold"
                  >
                    {{ formatBytes(chartData.total_storage) }}
                  </text>
                </svg>
              </template>
            </dx-pie-chart>
            <div
              v-if="showBuyMoreStorage"
              class="p-2"
            >
              <q-card class="bg-orange-1 border-4 border-dashed border-orange-7 cursor-pointer">
                <q-card-section class="flex items-center justify-between">
                  <span class="text-orange-10 font-semibold underline-offset-4">
                    {{ i18n.t('common.buyMoreStorage') }}
                  </span>
                  <q-icon
                    name="mdi-cart-plus"
                    class="text-orange-9"
                    size="xs"
                  />
                </q-card-section>
              </q-card>
            </div>
          </div>
        </div>
      </dx-item>
      <dx-item
        min-size="21%"
        :collapsible="true"
        collapsed-size="21%"
      >
        <dx-data-grid
          ref="gridRef"
          v-bind="gridOptions"
        >
          <template #attachmentTagCellTemplate="{ data: templateOptions }">
            <q-chip
              size="sm"
              text-color="white"
              style="margin: 0"
              :label="portalStore.getLocalizedAttachmentTags().find(x => x.id === templateOptions.data.attachment_tag)?.name"
              :color="portalStore.getLocalizedAttachmentTags().find(x => x.id === templateOptions.data.attachment_tag)?.color"
            />
          </template>
          <template #attachmentFileNameTemplate="{ data: cellInfo }">
            <div>
              <a
                href="#"
                style="text-decoration: underline"
                @click.prevent="openFile(cellInfo.data)"
              >
                {{ cellInfo.data.attachment_file_name }}
              </a>
              <q-tooltip
                self="top middle"
                style="background-color: transparent"
              >
                <q-card>
                  <attachment-preview
                    :id="cellInfo.data.id"
                    :data="cellInfo.data"
                  />
                </q-card>
              </q-tooltip>
            </div>
          </template>
          <template #customToolbarDeleteButtonTemplate>
            <custom-toolbar-delete-button-template
              :selected-rows-id="selectedRows?.map(i => i.id) || []"
              :grid="gridRef"
              :on-delete="batchDeleteAsync"
              :disabled="selectedRows.length === 0"
            />
          </template>
          <template #documentsRef="{ data: cellInfo }">
            <document-reference-template :cell-info="cellInfo" />
          </template>
        </dx-data-grid>
      </dx-item>
    </dx-splitter>
  </div>
</template>

<style scoped lang="scss">
</style>
